import { axisFont } from "../../text/axisFont";
import { roundedRectPath } from "../../text/roundedRect";
import { formatPrice } from "../../axes/priceFormat";
import { clampBadgeY, drawPriceBadge, priceBadgeRect } from "../priceBadge";
import {
  POSITION_DIRECTION,
  POSITION_PILL_LEFT_PX,
  POSITION_PIN_ARROW,
  TONE_COLORS,
  ZERO_PRICE_TEXT,
} from "./positionConstants";

/**
 * Small triangle right of a pinned pill (and left of the pinned last price
 * badge): ▲ when the real price is above, ▼ below.
 *
 * @param {CanvasRenderingContext2D} context
 * @param {{ x: number, centreY: number, pin: string, color: string }} arrow - x of its left edge
 */
export const drawPinArrow = (context, { x, centreY, pin, color }) => {
  const { width, height } = POSITION_PIN_ARROW;
  const isUp = pin === POSITION_DIRECTION.UP;
  const baseY = centreY + (isUp ? height : -height) / 2;
  const tipY = centreY - (isUp ? height : -height) / 2;

  context.beginPath();
  context.moveTo(x, baseY);
  context.lineTo(x + width, baseY);
  context.lineTo(x + width / 2, tipY);
  context.closePath();
  context.fillStyle = color;
  context.fill();
};

/**
 * Fully rounded pill of a line on the left, right on its line (pills of
 * close lines overlap, as on moon.com): "Entry", "Take Profit - 18.018%",
 * "Stop Loss - 10.8108%", "Liquidation"; with ▲ / ▼ after it when the line is pinned.
 *
 * @param {object} textLayer
 * @param {{ tone: string, text: string, y: number, pin: string|null, pillWidth: number }} line -
 *   from resolvePositionScene
 * @param {{ font: string, sizes: object, colors: object }} style
 */
export const drawPill = (textLayer, line, { font, sizes, colors }) => {
  const { context } = textLayer;
  const toneColors = TONE_COLORS[line.tone];
  const rect = {
    x: POSITION_PILL_LEFT_PX,
    y: line.y - sizes.pillHeight / 2,
    width: line.pillWidth,
    height: sizes.pillHeight,
  };

  roundedRectPath(context, rect, rect.height / 2);
  context.fillStyle = colors[toneColors.line].css;
  context.fill();
  textLayer.drawText(line.text, rect.x + sizes.pillPaddingX, line.y, {
    font,
    color: colors[toneColors.text].css,
    align: "left",
  });

  if (line.pin) {
    drawPinArrow(context, {
      x: rect.x + rect.width + POSITION_PIN_ARROW.gap,
      centreY: line.y,
      pin: line.pin,
      color: colors[toneColors.line].css,
    });
  }
};

/**
 * Price badges of the position lines (entry, take profit, stop loss,
 * liquidation) on the price axis, in the colour of the line; the badge of a
 * line pinned to the edge is pressed to the top / bottom of the axis. Each stays on its price and they overlap, like the
 * pills (moon.com); the last price badge, drawn later, covers them.
 *
 * @param {object} textLayer
 * @param {Array<{ tone: string, price: number, y: number, pin: string|null }>} lines - from resolvePositionScene
 * @param {object} params
 * @param {{ width: number, height: number }} params.pane
 * @param {number} params.priceAxisWidth
 * @param {number} params.decimals - price precision of the pair
 * @param {{ colors: object, fontFamily: string }} params.theme
 */
export const drawPositionPriceBadges = (
  textLayer,
  lines,
  { pane, priceAxisWidth, decimals, theme },
) => {
  // A pinned line has y on the pane edge, so its badge is pressed to the edge of the axis
  const badges = lines.map((line) => ({ line, rect: priceBadgeRect(clampBadgeY(line.y, pane), pane, priceAxisWidth) }));
  const font = axisFont(theme.fontFamily);

  badges.forEach(({ line, rect }) => {
    const toneColors = TONE_COLORS[line.tone];

    drawPriceBadge(textLayer, rect, line.price === 0 ? ZERO_PRICE_TEXT : formatPrice(line.price, decimals), {
      font,
      background: theme.colors[toneColors.line].css,
      color: theme.colors[toneColors.text].css,
    });
  });
};
